import { useContext, useId } from "react";
import type { PaginationComponentProps } from "react-data-table-component";
import { useTranslation } from "react-i18next";
import { Dropdown } from "./Dropdown";
import { TablePagerContext } from "./tablePagerContext";
import styles from "./TablePager.module.css";
import { Tooltip } from "./Tooltip";

/**
 * Every table's pager, in place of react-data-table-component's own, whose rows-per-page choice is the browser's select: anything the
 * table hands down at the left (`TablePagerContext`); at the right, the rows-per-page choice as the portal's own pill-shaped dropdown,
 * the range shown, and the first, previous, next and last page buttons. The rows-per-page menu opens upward, because the table's
 * card would clip it opening downward. Pass it as a table's `paginationComponent`.
 *
 * @param props - The pager props react-data-table-component passes to a custom pager.
 */
export function TablePager({
  rowsPerPage,
  rowCount,
  currentPage,
  onChangePage,
  onChangeRowsPerPage,
  paginationRowsPerPageOptions = [],
}: PaginationComponentProps): React.JSX.Element {
  const { t } = useTranslation();
  const rowsPerPageId = useId();
  const { leading, dense } = useContext(TablePagerContext);
  const first = rowCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const last = Math.min(currentPage * rowsPerPage, rowCount);
  const lastPage = Math.max(1, Math.ceil(rowCount / rowsPerPage));

  return (
    <div className={dense ? `${styles.pager} ${styles.dense}` : styles.pager}>
      {leading}
      <div className={styles.controls}>
        <label htmlFor={rowsPerPageId} className={styles.label}>
          {t("dataTable.rowsPerPage")}
        </label>
        <Dropdown
          id={rowsPerPageId}
          placement="above"
          size="small"
          shape="pill"
          options={paginationRowsPerPageOptions.map((option) => ({ value: String(option), label: String(option) }))}
          value={String(rowsPerPage)}
          onChange={(value) => onChangeRowsPerPage(Number(value), currentPage)}
        />
        <span className={styles.range}>{t("dataTable.range", { first, last, count: rowCount })}</span>
        <div className={styles.pages}>
          <PagerButton label={t("dataTable.firstPage")} disabled={currentPage <= 1} onClick={() => onChangePage(1, rowCount)} direction="first" />
          <PagerButton label={t("dataTable.previousPage")} disabled={currentPage <= 1} onClick={() => onChangePage(currentPage - 1, rowCount)} direction="previous" />
          <PagerButton label={t("dataTable.nextPage")} disabled={currentPage >= lastPage} onClick={() => onChangePage(currentPage + 1, rowCount)} direction="next" />
          <PagerButton label={t("dataTable.lastPage")} disabled={currentPage >= lastPage} onClick={() => onChangePage(lastPage, rowCount)} direction="last" />
        </div>
      </div>
    </div>
  );
}

/** The icon paths of each page button: a chevron, with a bar at the end for the first and last page (|< and >|). */
const pagerIcons: Record<"first" | "previous" | "next" | "last", string[]> = {
  first: ["M3.5 4v8", "M11 4l-4 4 4 4"],
  previous: ["M10 4l-4 4 4 4"],
  next: ["M6 4l4 4-4 4"],
  last: ["M6 4l4 4-4 4", "M12.5 4v8"],
};

/** One of the pager's page buttons. */
function PagerButton({
  label,
  disabled,
  onClick,
  direction,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  direction: "first" | "previous" | "next" | "last";
}): React.JSX.Element {
  return (
    <Tooltip label={label}>
      <button type="button" className={styles.button} aria-label={label} disabled={disabled} onClick={onClick}>
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          {pagerIcons[direction].map((d) => (
            <path key={d} d={d} fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          ))}
        </svg>
      </button>
    </Tooltip>
  );
}
